import {
  AlertTriangleIcon,
  CheckCircleIcon,
  DownloadIcon,
  RefreshCwIcon,
  UploadIcon,
} from 'lucide-react';
import type React from 'react';
import { useRef, useState } from 'react';
import type { z } from 'zod';
import type { Novel } from '../../data/samples';
import { BackupDataSchema } from '../../utils/validation/storageSchemas';

interface DataManagementTabProps {
  isDark: boolean;
  customNovels: Novel[];
  updateCustomNovels: (novels: Novel[]) => Promise<void>;
}

type BackupData = z.infer<typeof BackupDataSchema>;

export const DataManagementTab: React.FC<DataManagementTabProps> = ({
  isDark,
  customNovels,
  updateCustomNovels,
}) => {
  const [status, setStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [errorMsg, setErrorMsg] = useState('');
  const fileInputRef = useRef<HTMLInputElement>(null);

  const activeBtnBg = isDark
    ? 'bg-neutral-700 text-[#FAF8F5] hover:bg-neutral-600'
    : 'bg-[#111111] text-[#FFFFFF] hover:bg-neutral-800 shadow-xs';

  const borderCol = isDark ? 'border-neutral-800' : 'border-neutral-200';
  const cardBg = isDark ? 'bg-[#1C1C1E]' : 'bg-[#F9F9F8]';

  // 1. バックアップのエクスポート
  const handleExport = () => {
    try {
      const keysToBackup = [
        'novelflow_reading_history',
        'novelflow_cover_configs',
        'novelflow_fontSize',
        'novelflow_horizontal_fontSize',
        'novelflow_lineHeight',
        'novelflow_horizontal_lineHeight',
        'novelflow_fontFamily',
        'novelflow_margin',
        'novelflow_pageTransition',
        'novelflow_bookModeEnabled',
        'novelflow_hapticsEnabled',
        'novelflow_soundVolume',
        'novelflow_bgmEnabled',
        'novelflow_bgmVolume',
        'novelflow_bgmSound',
        'novelflow_glowEnabled',
        'novelflow_glowIntensity',
        'novelflow_focusModeEnabled',
        'novelflow_focusLineCount',
        'novelflow_tateChuYokoEnabled',
        'novelflow_radialMenuEnabled',
        'novelflow_bookmarkGestureEnabled',
        'novelflow_readingSpeed',
        'novelflow_shareCardEnabled',
        'novelflow_rubyDisplay',
        'novelflow_showRemainingTime',
        'novelflow_current_reading_speed',
        'novelflow_reading_speed_history',
        'novelflow_emphasisDotsStyle',
      ];

      const localStorageData: Record<string, string> = {};
      for (const key of keysToBackup) {
        const val = localStorage.getItem(key);
        if (val !== null) {
          localStorageData[key] = val;
        }
      }

      const backup: BackupData = {
        version: '1.0',
        exportedAt: new Date().toISOString(),
        customNovels,
        localStorageData,
      };

      const jsonStr = JSON.stringify(backup, null, 2);
      const blob = new Blob([jsonStr], { type: 'application/json' });
      const url = URL.createObjectURL(blob);

      const dateStr = new Date().toISOString().slice(0, 10).replace(/-/g, '');
      const filename = `novelflow_backup_${dateStr}.json`;

      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();

      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (e) {
      console.error('Export failed:', e);
      alert('バックアップの出力に失敗しました。');
    }
  };

  // 2. バックアップのインポート
  const handleImportClick = () => {
    fileInputRef.current?.click();
  };

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setStatus('idle');
    setErrorMsg('');

    try {
      const text = await file.text();
      const rawData = JSON.parse(text);
      const result = BackupDataSchema.safeParse(rawData);

      // バリデーション
      if (!result.success) {
        console.warn('Backup data validation failed:', result.error);
        throw new Error('ファイルの形式が正しくありません。');
      }

      const backup = result.data;

      const confirmRestore = window.confirm(
        `バックアップファイル「${file.name}」から復元します。\n現在のすべての小説データ、しおり、読書設定が上書きされます。よろしいですか？`,
      );

      if (!confirmRestore) {
        if (fileInputRef.current) fileInputRef.current.value = '';
        return;
      }

      // 復元処理
      // IndexedDBへカスタム小説を保存
      await updateCustomNovels(backup.customNovels as Novel[]);

      // LocalStorageへ設定を書き戻し
      for (const [key, value] of Object.entries(backup.localStorageData)) {
        localStorage.setItem(key, value);
      }

      setStatus('success');

      // リロードを促す
      setTimeout(() => {
        window.location.reload();
      }, 1500);
    } catch (err: unknown) {
      console.error('Import failed:', err);
      setStatus('error');
      setErrorMsg(err instanceof Error ? err.message : 'バックアップの復元に失敗しました。');
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  return (
    <div className="space-y-5">
      <span className="text-[10px] font-bold tracking-widest uppercase opacity-45 block border-b pb-1 border-current/10">
        データ管理
      </span>

      <div className={`p-4 rounded-xl border ${borderCol} ${cardBg} space-y-4`}>
        <div className="space-y-1">
          <h4 className="text-xs font-bold flex items-center gap-1.5">
            <DownloadIcon size={14} />
            データのエクスポート
          </h4>
          <p className="text-[10px] opacity-50 leading-relaxed">
            本棚の全小説データ（カスタム小説）、読書履歴、設定をJSONファイルとして保存します。
          </p>
        </div>
        <button
          type="button"
          onClick={handleExport}
          className={`w-full py-2.5 rounded-lg text-xs font-bold transition-all active:scale-98 flex items-center justify-center gap-2 ${activeBtnBg}`}
        >
          <DownloadIcon size={14} />
          <span>バックアップをダウンロード</span>
        </button>
      </div>

      <div className={`p-4 rounded-xl border ${borderCol} ${cardBg} space-y-4`}>
        <div className="space-y-1">
          <h4 className="text-xs font-bold flex items-center gap-1.5">
            <UploadIcon size={14} />
            データのインポート（復元）
          </h4>
          <p className="text-[10px] opacity-50 leading-relaxed">
            以前に出力したバックアップファイルを読み込んで、現在の本棚・設定状態を復元します。
          </p>
        </div>

        <input
          type="file"
          ref={fileInputRef}
          onChange={handleFileChange}
          accept=".json"
          className="hidden"
        />

        <button
          type="button"
          onClick={handleImportClick}
          className={`w-full py-2.5 rounded-lg text-xs font-bold transition-all active:scale-98 border flex items-center justify-center gap-2 ${
            isDark
              ? 'border-neutral-700 bg-neutral-900/60 hover:bg-neutral-850 text-neutral-200'
              : 'border-neutral-300 bg-white hover:bg-neutral-50 text-neutral-800'
          }`}
        >
          <UploadIcon size={14} />
          <span>バックアップファイルを復元</span>
        </button>

        {status === 'success' && (
          <div className="flex items-center gap-2 text-xs font-bold text-green-600 dark:text-green-400 bg-green-500/10 p-3 rounded-lg border border-green-500/20">
            <CheckCircleIcon size={14} className="shrink-0 animate-bounce" />
            <div className="flex items-center gap-1.5">
              <span>復元に成功しました。再読み込み中...</span>
              <RefreshCwIcon size={12} className="animate-spin" />
            </div>
          </div>
        )}

        {status === 'error' && (
          <div className="flex items-start gap-2 text-xs font-bold text-red-600 dark:text-red-400 bg-red-500/10 p-3 rounded-lg border border-red-500/20">
            <AlertTriangleIcon size={14} className="shrink-0 mt-0.5" />
            <div>
              <p>復元エラー</p>
              <p className="text-[10px] font-normal opacity-85 mt-0.5">{errorMsg}</p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
